import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as api from '../api/api';
import { goalValueToDisplay, goalValueToStored, goalUnitLabel, formatGoalValue, summarizeGoalColumnTotals, columnLabel } from '../utils/GoalValueUtil';
import { formatDisplayDate } from '../utils/DateUtil';
import './Plan.css';

const mondayOf = (date) => {
    const day = date.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(date);
    monday.setDate(date.getDate() + diffToMonday);
    return monday;
};
const formatDateParam = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const thisWeekStartIso = formatDateParam(mondayOf(new Date()));
const formatShortDate = (isoDate) => new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
// Day-level Time cells (a single session) read better as plain minutes than
// the hrs/mins breakdown used everywhere else in the plan.
const DAY_TIME_OPTIONS = { timeInMinutes: true };

const PlanWeekView = () => {
    const { weekStartDate: weekStartDateParam } = useParams();
    const navigate = useNavigate();
    const weekStartDate = weekStartDateParam || thisWeekStartIso;

    const [plan, setPlan] = useState(null);
    const [unitSettings, setUnitSettings] = useState({ distance_unit: 'KILOMETRES', elevation_unit: 'METRES' });
    const [isLoading, setIsLoading] = useState(true);
    const [isClearing, setIsClearing] = useState(false);
    const [error, setError] = useState('');
    // The day Plan inputs are uncontrolled (defaultValue), so emptying them
    // all at once needs the inputs themselves replaced - bumping this is
    // part of their key, which is what makes React mount fresh ones.
    const [clearCount, setClearCount] = useState(0);
    const todayRowRef = useRef(null);
    const hasScrolledToTodayRef = useRef(false);

    useEffect(() => {
        // Falls back to the hardcoded KILOMETRES/METRES defaults already in
        // state on failure - unit labels being briefly wrong is better than
        // this failing silently with no indication settings didn't load.
        api.getUserSettings().catch((e) => {
            console.error('Failed to load unit settings:', api.getErrorMessage(e, e));
        }).then((settings) => settings && setUnitSettings(settings));
    }, []);

    useEffect(() => {
        hasScrolledToTodayRef.current = false;
        setIsLoading(true);
        setError('');
        api.getWeekPlan(weekStartDate)
            .then(({ data }) => setPlan(data))
            .catch((e) => setError(api.getErrorMessage(e, 'Unable to load this week\'s plan.')))
            .finally(() => setIsLoading(false));
    }, [weekStartDate]);

    // Only jump to today when viewing the current week — a past/future
    // week's own row order is what matters there, not scroll position.
    useEffect(() => {
        if (!isLoading && plan && !hasScrolledToTodayRef.current && todayRowRef.current) {
            todayRowRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
            hasScrolledToTodayRef.current = true;
        }
    }, [isLoading, plan]);

    const saveWeekDetails = (updatedFields) => {
        const nextPlan = { ...plan, ...updatedFields };
        setPlan(nextPlan);
        api.updateWeekPlan(weekStartDate, {
            event: nextPlan.event,
            notes: nextPlan.notes,
            diary: nextPlan.diary,
        }).then(({ data }) => setPlan(data)).catch((e) => setError(api.getErrorMessage(e, 'Unable to save changes.')));
    };

    const saveDayField = (dayIndex, updatedFields) => {
        const nextDays = plan.days.map((day, index) => index === dayIndex ? { ...day, ...updatedFields } : day);
        const nextPlan = { ...plan, days: nextDays };
        setPlan(nextPlan);
        const day = nextDays[dayIndex];
        api.updateDayPlan(day.date, {
            extras: day.extras,
        }).then(({ data }) => setPlan(data)).catch((e) => setError(api.getErrorMessage(e, 'Unable to save changes.')));
    };

    const saveDayGoalValue = (dayIndex, goalId, plannedValue) => {
        const day = plan.days[dayIndex];
        // Re-fetch the whole week rather than splicing the single updated
        // goal value into state: the save response only carries that one
        // day's planned/actual value, and the week total's Planned/Projected
        // figure lives on the separate plan.weekGoalValues array, which a
        // partial merge into plan.days never touches at all.
        api.updateDayGoalValue(day.date, goalId, plannedValue)
            .then(() => api.getWeekPlan(weekStartDate))
            .then(({ data }) => setPlan(data))
            .catch((e) => setError(api.getErrorMessage(e, 'Unable to save changes.')));
    };

    // Only offered for a week that can still honestly be re-planned - the
    // backend decides that (canClearGoalPlans) and enforces it, so the two
    // can't drift apart. Only the days' planned numbers go: the week's own
    // planned value, the event, notes, diary and each day's session details
    // are all left alone.
    const clearDayGoalValues = () => {
        if (!window.confirm('Clear the planned value on every day of this week?\n\nThe week\'s own planned value is kept, along with the event, notes, diary and session details - reloading the week splits it back across the days.')) return;
        setIsClearing(true);
        setError('');
        api.clearWeekDayGoalValues(weekStartDate)
            .then(({ data }) => {
                setPlan(data);
                setClearCount((count) => count + 1);
            })
            .catch((e) => setError(api.getErrorMessage(e, 'Unable to clear this week\'s day plan values.')))
            .finally(() => setIsClearing(false));
    };

    const goToWeek = (newWeekStart) => navigate(`/plan/week/${newWeekStart}`);
    const goToPreviousWeek = () => plan && goToWeek(plan.previousWeekStartDate);
    const goToNextWeek = () => plan && goToWeek(plan.nextWeekStartDate);
    const goToThisWeek = () => goToWeek(thisWeekStartIso);

    if (isLoading) return <main className="plan-page"><p className="plan-message">Loading week plan...</p></main>;
    if (error && !plan) return <main className="plan-page"><p className="plan-message">{error}</p></main>;
    if (!plan) return null;

    // Nothing left to project once the week itself has already finished —
    // the total row's label reflects that instead of calling a settled
    // number "Projected".
    const isWeekFullyPast = plan.weekEndDate < formatDateParam(new Date());

    return (
        <main className="plan-page">
            <header className="plan-heading">
                <div>
                    <span className="eyebrow">Week plan</span>
                    <h1>{formatDisplayDate(`${plan.weekStartDate}T00:00:00`)} – {formatDisplayDate(`${plan.weekEndDate}T00:00:00`)}</h1>
                </div>
                <div className="plan-week-nav">
                    <button type="button" onClick={goToPreviousWeek}>‹ Previous</button>
                    <button type="button" onClick={goToThisWeek} disabled={weekStartDate === thisWeekStartIso}>This week</button>
                    <button type="button" onClick={goToNextWeek}>Next ›</button>
                    <Link to={`/plan/year/${plan.weekStartDate.slice(0, 4)}`} className="plan-year-link">View year ›</Link>
                    {plan.canClearGoalPlans && (
                        <button type="button" className="plan-clear-button" onClick={clearDayGoalValues} disabled={isClearing}>
                            {isClearing ? 'Clearing...' : '✕ Clear day plan values'}
                        </button>
                    )}
                </div>
            </header>

            <div className="plan-table-wrap">
                <table className="plan-table">
                    <thead>
                        <tr>
                            <th>Day</th>
                            {plan.columns.map((column) => <th key={column.columnKey}>{columnLabel(column)} ({goalUnitLabel(column.goalUnit, unitSettings)})</th>)}
                            <th className="plan-extras-cell">Extras</th>
                        </tr>
                    </thead>
                    <tbody>
                        {plan.days.map((day, index) => (
                            <tr key={day.date} ref={day.isToday ? todayRowRef : undefined} className={day.isToday ? 'plan-row-today' : ''}>
                                <td className="plan-day-cell">
                                    {/* The day is the way through to what was
                                        actually done on it - the activity list
                                        opens at this date. */}
                                    <Link to={`/activity?date=${day.date}`} className="plan-jump-button" title={`View activities for ${day.dayName} ${formatShortDate(day.date)}`}>
                                        <span className="plan-jump-button-main">{day.dayName}</span>
                                        <span className="plan-jump-button-sub">{formatShortDate(day.date)}</span>
                                        <span className="plan-jump-button-chevron" aria-hidden="true">›</span>
                                    </Link>
                                    {day.isToday && <small>today</small>}
                                </td>
                                {plan.columns.map((column) => {
                                    const goalValue = day.goalValues.find((value) => value.columnKey === column.columnKey);
                                    if (!goalValue) return <td key={column.columnKey} className="plan-cell-inactive" />;
                                    return (
                                        <td key={column.columnKey} className="plan-goal-cell">
                                            <label className="plan-goal-subfield">
                                                <small>Plan</small>
                                                <span className="plan-input-with-unit">
                                                    <input
                                                        key={`${column.columnKey}-${goalValue.goalId}-${clearCount}`}
                                                        type="number"
                                                        className="plan-value-input"
                                                        step="0.1"
                                                        min="0"
                                                        defaultValue={goalValueToDisplay(goalValue.plannedValue, column.goalUnit, unitSettings, DAY_TIME_OPTIONS) ?? ''}
                                                        onBlur={(event) => saveDayGoalValue(index, goalValue.goalId, goalValueToStored(event.target.value, column.goalUnit, unitSettings, DAY_TIME_OPTIONS))}
                                                    />
                                                    <span className="plan-input-unit">{goalUnitLabel(column.goalUnit, unitSettings, DAY_TIME_OPTIONS)}</span>
                                                </span>
                                            </label>
                                            <div className="plan-goal-subfield plan-goal-actual">
                                                <small>Actual</small>
                                                <span>{formatGoalValue(goalValue.actualValue, column.goalUnit, unitSettings, DAY_TIME_OPTIONS)}</span>
                                            </div>
                                        </td>
                                    );
                                })}
                                <td className="plan-extras-cell">
                                    <textarea rows={2} defaultValue={day.extras || ''} onBlur={(event) => saveDayField(index, { extras: event.target.value })} placeholder="Session details" />
                                </td>
                            </tr>
                        ))}
                        <tr className="plan-total-row">
                            <td>Total for week</td>
                            {plan.columns.map((column) => {
                                const columnGoalValues = plan.days.flatMap((day) => day.goalValues.filter((value) => value.columnKey === column.columnKey));
                                // This is the same past-days-actual / today-max(plan,actual)
                                // / future-days-plan blend the year view's totals row
                                // uses for a week row, computed once on the backend
                                // against plan.weekGoalValues — not a literal sum of
                                // the day cells' Plan values, which read as blank for
                                // most already-past days. Once the week itself has
                                // finished there's nothing left to blend, so it shows
                                // what was planned — hence isWeekFullyPast below.
                                const weekGoalValue = plan.weekGoalValues?.find((value) => value.columnKey === column.columnKey);
                                // The goal's target is set for its whole span (e.g. a
                                // year), so a single week is judged against a fair
                                // one-week share of it — weeklyTargetValue — rather
                                // than the full target, which the year view already
                                // compares against.
                                const summary = summarizeGoalColumnTotals(
                                    [isWeekFullyPast ? weekGoalValue?.plannedValue : weekGoalValue?.projectedValue],
                                    columnGoalValues.map((value) => value.actualValue),
                                    column.weeklyTargetValue,
                                    column.goalUnit,
                                    unitSettings,
                                );
                                return (
                                    <td key={column.columnKey} className="plan-goal-cell">
                                        <div className="plan-goal-subfield"><small>Target{summary.targetPercent ? ` (${summary.targetPercent})` : ''}</small><span>{summary.target}</span></div>
                                        <div className="plan-goal-subfield"><small>{isWeekFullyPast ? 'Planned' : 'Projected'}</small><span>{summary.plan}</span></div>
                                        <div className="plan-goal-subfield plan-goal-actual"><small>Actual</small><span>{summary.actual}</span></div>
                                    </td>
                                );
                            })}
                            <td />
                        </tr>
                        {/* The week's own event/notes/diary are rows below the
                            days rather than a separate panel under the table,
                            so the table is the only thing on the page that
                            scrolls - the same scrollbox the year view uses,
                            which is what keeps the column headers sticky.
                            They stay last because the day-by-day numbers are
                            what's checked on every visit; this is
                            reference/journaling. */}
                        <tr className="plan-detail-row">
                            <td className="plan-detail-label">Event / race</td>
                            <td colSpan={plan.columns.length + 1}>
                                <input type="text" defaultValue={plan.event || ''} onBlur={(event) => saveWeekDetails({ event: event.target.value })} placeholder="e.g. Galway 5k" />
                            </td>
                        </tr>
                        <tr className="plan-detail-row">
                            <td className="plan-detail-label">Plan notes</td>
                            <td colSpan={plan.columns.length + 1}>
                                <textarea defaultValue={plan.notes || ''} onBlur={(event) => saveWeekDetails({ notes: event.target.value })} placeholder="Session plan for the week, e.g. race week, low mileage reason..." />
                            </td>
                        </tr>
                        <tr className="plan-detail-row">
                            <td className="plan-detail-label">Retrospective diary</td>
                            <td colSpan={plan.columns.length + 1}>
                                <textarea defaultValue={plan.diary || ''} onBlur={(event) => saveWeekDetails({ diary: event.target.value })} placeholder="How the week actually went..." />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {error && <p className="plan-message plan-error">{error}</p>}
        </main>
    );
};

export default PlanWeekView;
